import React, { useContext } from 'react';
import { Card } from 'antd';
import { HeartOutlined, RightOutlined } from '@ant-design/icons';
import { ThemeContext } from '../../context/ThemeContext';
import './ISINCard.css';

/** Gold crown on sovereign (govt) cards — decorative only, matches rating slot. */
const SovereignCrownIcon = () => (
  <svg
    className="isin-card-premium__sovereign-crown-svg"
    viewBox="0 0 48 40"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    focusable="false"
  >
    <path
      fill="#D4AF37"
      d="M5 34V26L9 30 12 15 15.5 24 23 9 30.5 24 34 15 37 30 41 26V34H5z"
    />
    <circle cx="12" cy="14" r="2.2" fill="#E8C547" />
    <circle cx="23" cy="8" r="2.2" fill="#E8C547" />
    <circle cx="34" cy="14" r="2.2" fill="#E8C547" />
    <circle cx="40" cy="24" r="2.2" fill="#E8C547" />
    <path fill="#C9A227" d="M9 31.5h30v4.5a2.5 2.5 0 0 1-2.5 2.5h-25A2.5 2.5 0 0 1 9 36v-4.5z" />
    <circle cx="16" cy="30" r="2" fill="#fff" opacity="0.92" />
    <circle cx="24" cy="28.5" r="2" fill="#fff" opacity="0.92" />
    <circle cx="32" cy="30" r="2" fill="#fff" opacity="0.92" />
  </svg>
);

const ISINCard = ({ isin, index, onViewDetails }) => {
  const { isDark } = useContext(ThemeContext);

  const lightGradients = [
    'linear-gradient(119.64deg, #064E3B 0%, #065F46 25%, #10B981 50%, #059669 75%, #064E3B 100%)',//AAA
    // 'linear-gradient(119.64deg, #FDFCE9 0%, #E6E4D6 25%, #FFFEFD 50%, #CBC8B7 75%, #E6E4D6 100%)', // AAA
    // 'linear-gradient(119.64deg, #D4AF37 0%, #C5A028 25%, #F4E4BC 50%, #B8860B 75%, #A67C52 100%)', // AA
    'linear-gradient(119.64deg, #BF953F 0%, #FCF6BA 25%, #B38728 50%, #FBF5B7 75%, #AA771C 100%)',
    'linear-gradient(119.64deg, #F9FAFB 0%, #D1D5DB 25%, #FFFFFF 50%, #9CA3AF 75%, #D1D5DB 100%)'
  ];
  const govtGradient = 'radial-gradient(57.5% 101.28% at 50% 50%, #F8FAFC 0%, #E2E8F0 40%, #CBD5E1 100%)';
  const darkGradients = [
    'linear-gradient(129.09deg, #374865 0%, #26365D 50%, #020617 100%)', // BBB
    // 'linear-gradient(118deg, #F87171 0%, #DC2626 50%, #F87171 100%)', // Other
    'linear-gradient(118deg, #2a2f3d 0%, #1a1e28 50%, #232836 100%)'// Other
  ];

  const stripPpMld = (rating) => String(rating || '')
    .replace(/PP\s*-\s*MLD/gi, '')
    .replace(/PPMLD/gi, '')
    .replace(/\(\s*CE\s*\)/gi, '')
    .trim();

  const normalizeRating = (rating) => stripPpMld(rating).toUpperCase().replace(/\s+/g, '');

  const resolveCardGradientByRating = (rating) => {
    const normalized = normalizeRating(rating);
    if (normalized === 'AAA') return lightGradients[0];
    if (normalized === 'AA+' || normalized === 'AA') return lightGradients[1];
    if (normalized === 'AA-' || normalized === 'A+' || normalized === 'A' || normalized === 'A-') return lightGradients[2];
    if (normalized === 'BBB+' || normalized === 'BBB' || normalized === 'BBB-') return darkGradients[0];
    return darkGradients[1];
  };

  const isDarkGraniteRating = (rating) => {
    const normalized = normalizeRating(rating);
    return normalized === 'AAA'
      || normalized === 'BBB+'
      || normalized === 'BBB'
      || normalized === 'BBB-'
      || (
        normalized !== 'AAA'
      && normalized !== 'AA+'
      && normalized !== 'AA'
      && normalized !== 'AA-'
      && normalized !== 'A+'
      && normalized !== 'A'
      && normalized !== 'A-'
      );
  };

  const formatDateIndian = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatFaceValueInr = (value) => {
    if (value == null || value === '') return 'N/A';
    const numValue = parseFloat(value);
    if (!Number.isFinite(numValue)) return 'N/A';
    if (numValue >= 10000000) {
      const crores = numValue / 10000000;
      return `₹${crores % 1 === 0 ? crores.toFixed(0) : crores.toFixed(1)} Cr`;
    }
    if (numValue >= 100000) {
      const lakhs = numValue / 100000;
      return `₹${lakhs % 1 === 0 ? lakhs.toFixed(0) : lakhs.toFixed(1)} Lac`;
    }
    return `₹${numValue.toLocaleString('en-IN')}`;
  };

  const getShortAgencyName = (fullName) => {
    if (!fullName) return fullName;
    const upperName = fullName.toUpperCase();
    if (upperName.includes('CRISIL')) return 'CRISIL';
    if (upperName.includes('ICRA')) return 'ICRA';
    if (upperName.includes('CARE') || upperName.includes('CREDIT ANALYSIS')) return 'CARE';
    if (upperName.includes('BRICKWORK')) return 'BRICKWORK';
    if (upperName.includes('INDIA RATING') || upperName.includes('INDIA RATINGS')) return 'INDIA';
    if (upperName.includes('ACUITE')) return 'ACUITE';
    if (upperName.includes('INFOMERICS')) return 'INFOMERICS';
    return fullName;
  };

  const pickPrimaryRating = (creditRatings) => {
    if (!creditRatings || !Array.isArray(creditRatings) || creditRatings.length === 0) return null;
    const sorted = [...creditRatings].filter(Boolean);
    const aaa = sorted.find((r) => normalizeRating(r?.currentRating).includes('AAA'));
    if (aaa) return aaa;
    return sorted[0];
  };

  const truncate = (value, maxLen = 15) => {
    if (value == null) return value;
    const s = String(value);
    if (s.length <= maxLen) return s;
    return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
  };

  const getInstrumentCategoryLabel = (govt) => {
    if (govt) {
      if (isin.securityType === 'State Government Securities') return 'STATE DEVELOPMENT LOAN';
      return 'GOVERNMENT BOND';
    }
    const st = (isin.securityType || '').trim();
    if (!st) return 'CORPORATE BOND';
    if (/corporate/i.test(st)) return 'CORPORATE BOND';
    return st.replace(/_/g, ' ').toUpperCase();
  };

  /**
   * Payment frequency: when interest is paid.
   * Single resolver that returns a user-friendly label (or '' if unknown).
   */
  const resolvePaymentFrequencyLabel = (value, govt) => {
    if (govt) return 'Paid semi-annually';
    if (!value) return '';
    const v = String(value).toLowerCase();
    if (v.includes('annually') || v.includes('annual') || v.includes('once a year')) return 'Paid annually';
    if (v.includes('twice a year') || v.includes('half-year') || v.includes('half yearly') || v.includes('semi')) return 'Paid half-yearly';
    if (v.includes('quarterly')) return 'Paid quarterly';
    if (v.includes('twelve times a year') || v.includes('monthly')) return 'Paid monthly';
    if (v.includes('on maturity')) return 'Paid on maturity';
    if (v.includes('irregular')) return 'Paid irregularly';
    return '';
  };

  const isCentralGovt = isin.securityType === 'Central Government Securities';
  const isStateGovt = isin.securityType === 'State Government Securities';
  const isGovt = isCentralGovt || isStateGovt;

  /** Central G-Sec: sovereign issuer; State SDL: show state (API: stateGovernment / companyIssuerName). */
  const issuerName = (() => {
    if (isStateGovt) {
      const state =
        (isin.stateGovernment && String(isin.stateGovernment).trim()) ||
        (isin.companyIssuerName && String(isin.companyIssuerName).trim());
      return state || '—';
    }
    if (isCentralGovt) return 'Government of India';
    return isin.companyIssuerName || '—';
  })();

  const primaryRating = pickPrimaryRating(isin.creditRatings);
  const normalizedPrimaryRating = normalizeRating(primaryRating?.currentRating);
  const backgroundColor = isGovt
    ? govtGradient
    : resolveCardGradientByRating(normalizedPrimaryRating);
  const useDarkGraniteTextScheme = !isGovt && isDarkGraniteRating(normalizedPrimaryRating);
  const hasCreditRating = !isGovt && Boolean(primaryRating?.currentRating);
  const ratingDetailLine = isGovt
    ? 'Sovereign'
    : primaryRating
      ? `${stripPpMld(primaryRating.currentRating)} by ${getShortAgencyName(primaryRating.creditRatingAgencyName)}`
      : '—';
  const ratingDetailLineShort = truncate(ratingDetailLine, 15);

  let couponDisplayValue = '—';
  let couponDisplaySuffix = '';
  if (isGovt && isin.nomenclature) {
    const firstPart = isin.nomenclature.split(' ')[0];
    if (firstPart && firstPart.includes('%')) {
      couponDisplayValue = firstPart.replace('%', '').trim() + '%';
      couponDisplaySuffix = 'P.A.';
    }
  } else {
    const couponBasisText = String(isin.couponBasis || '').trim();
    if (couponBasisText && /zero\s*interest/i.test(couponBasisText)) {
      couponDisplayValue = 'Zero Coupon';
      couponDisplaySuffix = '';
    } else if (couponBasisText && /variable/i.test(couponBasisText)) {
      couponDisplayValue = 'Variable';
      couponDisplaySuffix = '';
    } else if (isin.couponRate != null && isin.couponRate !== '') {
      const raw = typeof isin.couponRate === 'string' && String(isin.couponRate).includes('%')
        ? String(isin.couponRate).replace('%', '')
        : isin.couponRate;
      const numRate = parseFloat(raw);
      couponDisplayValue = Number.isFinite(numRate) ? `${numRate.toFixed(2)}%` : '—';
      couponDisplaySuffix = Number.isFinite(numRate) ? 'P.A.' : '';
    }
  }
  const isZeroCoupon = couponDisplayValue === 'Zero Coupon';

  const maturityRaw = isGovt
    ? (isin.redemption || isin.dateOfMaturity)
    : (isin.redemption || isin.dateOfRedemption || isin.maturityDate);

  const faceDisplay = isGovt
    ? (isin.faceValue != null ? formatFaceValueInr(isin.faceValue) : (isin.outstandingStock != null ? `₹${Number(isin.outstandingStock).toLocaleString('en-IN')} Cr` : '—'))
    : formatFaceValueInr(isin.faceValue);
  const paymentFrequencyLabel = resolvePaymentFrequencyLabel(isin.couponPaymentFrequency, isGovt);

  const handleDetails = (e) => {
    e?.stopPropagation?.();
    onViewDetails?.(isin);
  };

  return (
    <Card
      className={`bond-card isin-card isin-card--premium ${isGovt ? 'isin-card--sovereign' : ''} ${isDark ? 'dark' : ''} ${useDarkGraniteTextScheme ? 'isin-card--on-dark-granite' : ''}`}
      hoverable={!isGovt}
      onClick={isGovt ? undefined : handleDetails}
      style={{
        borderRadius: '20px',
        cursor: isGovt ? 'default' : 'pointer',
        background: isDark ? undefined : backgroundColor,
        '--card-bg-color': isDark ? 'transparent' : backgroundColor,
        '--isin-premium-bg': backgroundColor,
      }}
    >
      <div className="bond-card-inner isin-card-premium-inner">
        <div className="isin-card-premium__head">
          <div className="isin-card-premium__head-left">
            <h2 className="isin-card-premium__issuer">{issuerName}</h2>
            <p className="isin-card-premium__isin-line">
              ISIN:&nbsp;
              <span className="isin-card-premium__isin-code">{isin.isin || '—'}</span>
            </p>
          </div>
          {isGovt ? (
            <div className="isin-card-premium__head-right">
              <div
                className="isin-card-premium__sovereign-badge"
                title="Sovereign"
                role="img"
                aria-label="Sovereign security"
              >
                <SovereignCrownIcon />
              </div>
            </div>
          ) : hasCreditRating ? (
            <div className="isin-card-premium__head-right">
              <div className="isin-card-premium__rating-pill">
                <div className="isin-card-premium__shield" aria-hidden>
                  <HeartOutlined className="isin-card-premium__shield-heart" />
                </div>
                <div className="isin-card-premium__rating-copy">
                  <span className="isin-card-premium__rating-label">Credit rating</span>
                  <span className="isin-card-premium__rating-value" title={ratingDetailLine}>{ratingDetailLineShort}</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="isin-card-premium__grid">
          {/* <div className="isin-card-premium__cell isin-card-premium__cell--category">
            <span className="isin-card-premium__cell-label">Instrument category</span>
            <span className="isin-card-premium__cell-value">{getInstrumentCategoryLabel(isGovt)}</span>
          </div> */}
          <div className="isin-card-premium__cell isin-card-premium__cell--face">
            <span className="isin-card-premium__cell-label">Face value</span>
            <span className="isin-card-premium__cell-value">{faceDisplay}</span>
          </div>
          <div className="isin-card-premium__cell isin-card-premium__cell--maturity">
            <span className="isin-card-premium__cell-label">Maturity date</span>
            <span className="isin-card-premium__cell-value">{formatDateIndian(maturityRaw)}</span>
          </div>
        </div>

        <div className="isin-card-premium__footer">
          <div className="isin-card-premium__yield">
            {!isZeroCoupon ? (
              <span className="isin-card-premium__yield-label" title={paymentFrequencyLabel}>
                {paymentFrequencyLabel || '—'}
              </span>
            ) : null}
            <div className="isin-card-premium__yield-row">
              <span className="isin-card-premium__yield-pct">{couponDisplayValue}</span>
              {couponDisplaySuffix ? <span className="isin-card-premium__yield-pa">{couponDisplaySuffix}</span> : null}
            </div>
          </div>
          {!isGovt ? (
            <button
              type="button"
              className="isin-card-premium__cta"
              onClick={handleDetails}
            >
              View Details
              <RightOutlined className="isin-card-premium__cta-icon" />
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
};

export default ISINCard;
